import { IconContainer } from '@tabler/icons-react';
import { useTank } from '@/contexts/TankContext';
import { EmptyState } from './EmptyState';
import { TankSelector } from '../TankSelector';

interface NoTankSelectedProps {
  /** What the tank is needed for, e.g. "to see its devices". */
  purpose?: string;
}

/**
 * Actionable empty state shown when a page needs a tank but none is selected.
 *
 * - No tanks at all: prompts the user to create their first tank.
 * - Tanks exist but none selected: prompts the user to pick one, with an
 *   inline TankSelector.
 *
 * Renders nothing until the tank context has completed its first fetch, so it
 * never flashes on initial paint.
 */
export function NoTankSelected({ purpose = 'to continue' }: NoTankSelectedProps) {
  const { tanks, isInitialized } = useTank();

  if (!isInitialized) return null;

  if (tanks.length === 0) {
    return (
      <EmptyState
        icon={IconContainer}
        title="Create your first tank to start tracking"
        description="Everything in Reef DataHub — devices, tests, tasks — belongs to a tank."
        primaryAction={{ label: 'Create a tank', to: '/tanks' }}
        data-testid="no-tank-selected"
      />
    );
  }

  return (
    <EmptyState
      icon={IconContainer}
      title={`Pick a tank ${purpose}`}
      data-testid="no-tank-selected"
    >
      <TankSelector />
    </EmptyState>
  );
}
